/**
 * Morph Channel Definitions
 * 
 * Defines all anatomical morph channels for the fetal head model.
 * Each channel controls a specific anatomical region with weighted vertex influence.
 * 
 * Architecture: Similar to Blender Shape Keys / Blend Shapes
 */

// Morph channel types
export const MorphChannelType = {
  CRANIAL: 'cranial',      // Affects overall skull shape
  FACIAL: 'facial',        // Affects face region
  ORBITAL: 'orbital',      // Affects eye area
  NASAL: 'nasal',          // Affects nose area
  ORAL: 'oral',            // Affects mouth area
  MANDIBULAR: 'mandibular' // Affects jaw/chin area
};

/**
 * Morph Channel Definitions
 * 
 * Each channel defines:
 * - id: unique identifier
 * - name: display name (for debugging)
 * - type: anatomical category
 * - region: geometric region definition for vertex selection
 * - axis: primary deformation axis
 * - influence: how the deformation spreads (falloff)
 * - range: min/max morph values
 * - corrective: whether this channel has corrective smoothing
 */
export const MORPH_CHANNELS = {
  // === CRANIAL CHANNELS ===
  // INCREASED RANGES for visible FaceWarp deformations
  head_width: {
    id: 'head_width',
    name: 'Head Width (BPD)',
    type: MorphChannelType.CRANIAL,
    region: {
      // Affects all vertices, weighted by distance from Y-axis
      type: 'radial_from_axis',
      axis: 'y',
      falloff: 'linear'
    },
    axis: 'x',
    influence: { spread: 0.8, falloff: 0.4 },
    range: { min: -0.3, max: 0.4 },
    corrective: true
  },

  head_length: {
    id: 'head_length',
    name: 'Head Length (OFD)',
    type: MorphChannelType.CRANIAL,
    region: {
      type: 'radial_from_axis',
      axis: 'y',
      falloff: 'linear'
    },
    axis: 'z',
    influence: { spread: 0.8, falloff: 0.4 },
    range: { min: -0.25, max: 0.35 },
    corrective: true
  },

  head_height: {
    id: 'head_height',
    name: 'Head Height (HC related)',
    type: MorphChannelType.CRANIAL,
    region: {
      type: 'full',
      falloff: 'smooth'
    },
    axis: 'y',
    influence: { spread: 0.7, falloff: 0.35 },
    range: { min: -0.2, max: 0.3 },
    corrective: true
  },

  cranial_volume: {
    id: 'cranial_volume',
    name: 'Cranial Volume',
    type: MorphChannelType.CRANIAL,
    region: {
      // Upper half of head - cranium
      type: 'hemisphere',
      direction: 'upper',
      threshold: 0.1
    },
    axis: 'uniform',
    influence: { spread: 0.6, falloff: 0.5 },
    range: { min: -0.15, max: 0.25 },
    corrective: true
  },

  // === FACIAL CHANNELS ===
  forehead_width: {
    id: 'forehead_width',
    name: 'Forehead Width (Fromen)',
    type: MorphChannelType.FACIAL,
    region: {
      type: 'box',
      center: { x: 0, y: 0.35, z: 0.3 },
      size: { x: 0.8, y: 0.4, z: 0.5 },
      falloff: 'gaussian'
    },
    axis: 'x',
    influence: { spread: 0.5, falloff: 0.5 },
    range: { min: -0.2, max: 0.3 },
    corrective: true
  },

  forehead_projection: {
    id: 'forehead_projection',
    name: 'Forehead Projection',
    type: MorphChannelType.FACIAL,
    region: {
      type: 'box',
      center: { x: 0, y: 0.35, z: 0.5 },
      size: { x: 0.6, y: 0.35, z: 0.4 },
      falloff: 'gaussian'
    },
    axis: 'z',
    influence: { spread: 0.45, falloff: 0.55 },
    range: { min: -0.15, max: 0.25 },
    corrective: true
  },

  midface_width: {
    id: 'midface_width',
    name: 'Midface Width (BiocÇap)',
    type: MorphChannelType.FACIAL,
    region: {
      type: 'box',
      center: { x: 0, y: 0, z: 0.4 },
      size: { x: 0.9, y: 0.5, z: 0.5 },
      falloff: 'gaussian'
    },
    axis: 'x',
    influence: { spread: 0.5, falloff: 0.5 },
    range: { min: -0.2, max: 0.3 },
    corrective: true
  },

  // === ORBITAL CHANNELS ===
  eye_spacing: {
    id: 'eye_spacing',
    name: 'Eye Spacing (Göztepe)',
    type: MorphChannelType.ORBITAL,
    region: {
      type: 'dual_sphere',
      centers: [
        { x: -0.22, y: 0.12, z: 0.55 },
        { x: 0.22, y: 0.12, z: 0.55 }
      ],
      radius: 0.18,
      falloff: 'gaussian'
    },
    axis: 'x_outward', // Move away from center
    influence: { spread: 0.4, falloff: 0.6 },
    range: { min: -0.15, max: 0.2 },
    corrective: true
  },

  orbital_depth: {
    id: 'orbital_depth',
    name: 'Orbital Depth (Göz)',
    type: MorphChannelType.ORBITAL,
    region: {
      type: 'dual_sphere',
      centers: [
        { x: -0.22, y: 0.12, z: 0.55 },
        { x: 0.22, y: 0.12, z: 0.55 }
      ],
      radius: 0.15,
      falloff: 'gaussian'
    },
    axis: 'z',
    influence: { spread: 0.35, falloff: 0.65 },
    range: { min: -0.1, max: 0.15 },
    corrective: true
  },

  // === NASAL CHANNELS ===
  nose_projection: {
    id: 'nose_projection',
    name: 'Nose Projection (Burun)',
    type: MorphChannelType.NASAL,
    region: {
      type: 'sphere',
      center: { x: 0, y: -0.05, z: 0.7 },
      radius: 0.25,
      falloff: 'gaussian'
    },
    axis: 'z',
    influence: { spread: 0.5, falloff: 0.5 },
    range: { min: -0.15, max: 0.25 },
    corrective: true
  },

  nose_vertical: {
    id: 'nose_vertical',
    name: 'Nose Length',
    type: MorphChannelType.NASAL,
    region: {
      type: 'sphere',
      center: { x: 0, y: -0.08, z: 0.65 },
      radius: 0.22,
      falloff: 'gaussian'
    },
    axis: 'y',
    influence: { spread: 0.4, falloff: 0.6 },
    range: { min: -0.12, max: 0.18 },
    corrective: true
  },

  // === ORAL CHANNELS ===
  mouth_width: {
    id: 'mouth_width',
    name: 'Mouth Width (Ağızçapı)',
    type: MorphChannelType.ORAL,
    region: {
      type: 'box',
      center: { x: 0, y: -0.28, z: 0.55 },
      size: { x: 0.4, y: 0.15, z: 0.25 },
      falloff: 'gaussian'
    },
    axis: 'x',
    influence: { spread: 0.35, falloff: 0.65 },
    range: { min: -0.1, max: 0.15 },
    corrective: true
  },

  upper_lip_fullness: {
    id: 'upper_lip_fullness',
    name: 'Upper Lip (Üst Dudak)',
    type: MorphChannelType.ORAL,
    region: {
      type: 'box',
      center: { x: 0, y: -0.22, z: 0.58 },
      size: { x: 0.3, y: 0.1, z: 0.2 },
      falloff: 'gaussian'
    },
    axis: 'z',
    influence: { spread: 0.25, falloff: 0.75 },
    range: { min: -0.06, max: 0.1 },
    corrective: true
  },

  // === MANDIBULAR CHANNELS ===
  jaw_projection: {
    id: 'jaw_projection',
    name: 'Jaw Projection',
    type: MorphChannelType.MANDIBULAR,
    region: {
      type: 'box',
      center: { x: 0, y: -0.4, z: 0.35 },
      size: { x: 0.5, y: 0.3, z: 0.45 },
      falloff: 'gaussian'
    },
    axis: 'z',
    influence: { spread: 0.4, falloff: 0.6 },
    range: { min: -0.12, max: 0.2 },
    corrective: true
  },

  chin_length: {
    id: 'chin_length',
    name: 'Chin Length (Çene)',
    type: MorphChannelType.MANDIBULAR,
    region: {
      type: 'sphere',
      center: { x: 0, y: -0.5, z: 0.4 },
      radius: 0.3,
      falloff: 'gaussian'
    },
    axis: 'y',
    influence: { spread: 0.5, falloff: 0.5 },
    range: { min: -0.2, max: 0.35 },
    corrective: true
  },

  face_length: {
    id: 'face_length',
    name: 'Face Length (Fromen+Çene)',
    type: MorphChannelType.FACIAL,
    region: {
      type: 'face_front',
      zThreshold: 0.2,
      falloff: 'smooth'
    },
    axis: 'y',
    influence: { spread: 0.5, falloff: 0.5 },
    range: { min: -0.2, max: 0.3 },
    corrective: true
  }
};

/**
 * Get all morph channel IDs
 */
export const getAllChannelIds = () => Object.keys(MORPH_CHANNELS);

/**
 * Get morph channel by ID
 */
export const getChannelById = (id) => MORPH_CHANNELS[id];

/**
 * Get channels by type
 */
export const getChannelsByType = (type) => {
  return Object.values(MORPH_CHANNELS).filter(ch => ch.type === type);
};

export default MORPH_CHANNELS;
